import React from 'react';
import {
  FormControl,
  Radio,
  RadioGroup,
  FormLabel,
  VStack,
  Text,
  Checkbox,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';

import { ResearchComplexity } from '../../lib/pricing-tool-api/api';
import AbsoluteIntInput from '../../components/AbsNumberInput';

export default function ResearchComplexitySection({
  researchComplexity,
  setResearchComplexity,
  videoLength,
  setVideoLength,
  creativeTesting,
  setCreativeTesting,
  lowBandwidth,
  setLowBandwidth,
}: {
  researchComplexity: string;
  setResearchComplexity: (researchComplexity: ResearchComplexity) => void;
  videoLength: number;
  setVideoLength: (videoLength: number) => void;
  creativeTesting: boolean;
  setCreativeTesting: (creativeTesting: boolean) => void;
  lowBandwidth: boolean;
  setLowBandwidth: (lowBandwidth: boolean) => void;
}) {
  return (
    <VStack align="start" spacing="1rem">
      <FormControl>
        <FormLabel>
          Choose research complexity{' '}
          <Tooltip
            placement="right"
            hasArrow
            bg="white"
            color="gray.800"
            borderWidth="1px"
            borderColor="gray.200"
            label={
              <>
                Examples of research complexity:
                <br />• Significant amounts of survey logic
                <br />• Project requires significant analysis outside of crosstabs (ex: regressions or segmentation analysis)
                <br />• Project involves A/B testing outside of an in-survey A/B test (ex: A/B test of a client&apos;s actual on-the-ground program)
                <br />• Client is asking us to write message tests from scratch in a non-campaign setting
                <br />• Complex or niche topic without previously drafted questions
                <br />• Does NOT include fielding complexity
              </>
            }
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <Text fontSize="sm" color="gray.600" mb={2} textAlign="left">
          Complexity of research design as well as required research for drafting the survey.
        </Text>
        <RadioGroup value={researchComplexity} onChange={setResearchComplexity}>
          <VStack align="start">
            {Object.entries(ResearchComplexity).map(([key, value]) => {
              const displayText = {
                'standard': 'Standard',
                'high': 'High (1-2 extra complexities)',
                'ultra': 'Ultra (3+ extra complexities)'
              }[value] || key.charAt(0).toUpperCase() + key.slice(1);

              return (
                <Radio key={key} value={value}>
                  {displayText}
                </Radio>
              );
            })}
          </VStack>
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel>Creative Testing (video or image)</FormLabel>
        <Box textAlign="left">
          <Checkbox
            isChecked={creativeTesting}
            onChange={(e) => setCreativeTesting(e.target.checked)}
          >
            Yes
          </Checkbox>
        </Box>
      </FormControl>

      <FormControl>
        <FormLabel>Low Pollster Bandwidth</FormLabel>
        <Box textAlign="left">
          <Checkbox
            isChecked={lowBandwidth}
            onChange={(e) => setLowBandwidth(e.target.checked)}
          >
            Yes
          </Checkbox>
        </Box>
      </FormControl>

      <FormControl>
        <FormLabel>
          Video length in seconds{' '}
          <Tooltip
            placement="right"
            hasArrow
            bg="white"
            color="gray.800"
            borderWidth="1px"
            borderColor="gray.200"
            label="6% increase in recruitment cost for every 30 seconds a respondent sees"
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <AbsoluteIntInput
          numberVal={videoLength}
          setNumberVal={setVideoLength}
        />
      </FormControl>
    </VStack>
  );
}
